import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import DocumentToolbar from './DocumentToolbar';
import api from '../../services/api';

/**
 * Arrival notice preview.
 *
 * This page used to redraw the notice in HTML and let the toolbar download a
 * PDF the server drew separately. Two implementations of one document drift,
 * and by 2026-09-16 they had: the preview showed CONSIGNEE / SHIPPER / NOTIFY
 * as N/A (it read shipment-level columns, which ocean imports leave empty —
 * the parties live on the house bill), printed the internal `SHINE:SHSC…`
 * stamp in a REMARKS box, left PKG COUNT and CBM blank, showed no charges at
 * all, and even dated the shipment a day earlier than the PDF. The operator
 * downloaded the PDF for the same shipment and got a different document.
 *
 * So the preview IS the PDF now. One renderer, nothing to drift.
 */

function useShipmentType() {
  const { pathname } = useLocation();
  if (pathname.includes('/air-import/')) return 'air-import';
  if (pathname.includes('/ocean-import/')) return 'ocean-import';
  if (pathname.includes('/ocean-export/')) return 'ocean-export';
  if (pathname.includes('/misc/')) return 'misc';
  return 'truck';
}

const ArrivalNotice = () => {
  const { id } = useParams();
  const shipmentType = useShipmentType();

  const [shipment, setShipment] = useState(null);
  const [houses, setHouses] = useState([]);
  const [hblId, setHblId] = useState('');
  // Off by default. A notice that quietly carries figures is the expensive
  // mistake here: the agent's own charges are on the same shipment, and the
  // unfiltered render billed a consignee USD 220.00 that neither side owed.
  const [showFreight, setShowFreight] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/${shipmentType}/${id}`);
        if (cancelled) return;
        const raw = r.data || {};
        const hs = raw.hbls || raw.hawbs || [];
        setShipment(raw);
        setHouses(hs);
        // One house bill is one arrival notice. With several on a shipment and
        // no hbl_id the server prints every house's containers AND every
        // house's charges on one page, so default to the first rather than to
        // "all of them".
        if (hs.length) setHblId(String(hs[0].id));
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load shipment');
      }
    })();
    return () => { cancelled = true; };
  }, [id, shipmentType]);

  // Whose charges the notice may show. The A/R Fay raises carries
  // `bill_to` = the consignee, and the server only prints charges whose
  // bill_to matches what we pass — that filter is what keeps the overseas
  // agent's D/C off the customer's copy.
  const house = houses.find((h) => String(h.id) === String(hblId)) || {};
  const billToParty = (house.bill_to || house.consignee
                       || (shipment && (shipment.bill_to || shipment.consignee)) || '').trim();

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (hblId) p.set('hbl_id', hblId);
    // Always the customer copy: no internal memo, no CUSTOMER block naming the
    // agent. Charges appear only when asked for, and only the ones billed to
    // the party reading it.
    p.set('copy', 'customer');
    if (showFreight && billToParty) p.set('bill_to', billToParty);
    return p.toString();
  }, [hblId, showFreight, billToParty]);

  const docPath = `/pdf/shipment/${shipmentType}/${id}/arrival-notice`;
  const pdfUrlForToolbar = `${docPath}?${params()}`;

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api.get(pdfUrlForToolbar, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(
          new Blob([r.data], { type: 'application/pdf' }));
        setPdfUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setError('Failed to render the arrival notice');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      // Revoke on the next tick so the iframe has finished with it.
      if (objectUrl) setTimeout(() => window.URL.revokeObjectURL(objectUrl), 5000);
    };
  }, [pdfUrlForToolbar]);

  const fileName = `AN-${(shipment && shipment.file_no) || id}.pdf`;

  return (
    <>
      <DocumentToolbar
        title="Arrival Notice"
        pdfUrl={pdfUrlForToolbar}
        fileName={fileName}
        onClose={() => window.close()}
      />

      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
        padding: '10px 20px', background: '#f1f5f9',
        borderBottom: '1px solid #e2e8f0', fontSize: 13, color: '#334155',
      }}>
        {houses.length > 1 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600 }}>House B/L</span>
            <select value={hblId} onChange={(e) => setHblId(e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: 6,
                             border: '1px solid #cbd5e1', fontSize: 13 }}>
              {houses.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.hbl_no || h.hawb_no || `#${h.id}`}
                </option>
              ))}
            </select>
            <span style={{ color: '#64748b' }}>
              one house bill is one notice
            </span>
          </label>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                        cursor: billToParty ? 'pointer' : 'not-allowed' }}>
          <input type="checkbox" checked={showFreight} disabled={!billToParty}
                 onChange={(e) => setShowFreight(e.target.checked)} />
          <span style={{ fontWeight: 600 }}>Show freight charges</span>
        </label>

        {showFreight && billToParty && (
          <span style={{ color: '#64748b' }}>
            showing only charges billed to <b>{billToParty}</b>
          </span>
        )}
        {!billToParty && (
          <span style={{ color: '#b45309' }}>
            no bill-to party on this shipment — charges cannot be filtered, so
            they stay off
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: 24, color: '#b91c1c' }}>{error}</div>
      )}
      {loading && !error && (
        <div style={{ padding: 24, color: '#64748b' }}>Rendering…</div>
      )}
      {pdfUrl && !error && (
        <iframe
          title="Arrival Notice"
          src={pdfUrl}
          style={{ width: '100%', height: 'calc(100vh - 110px)', border: 'none' }}
        />
      )}
    </>
  );
};

export default ArrivalNotice;
